import { Add } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { PaginationLoadingOverlay } from "../clients/PaginationLoadingOverlay";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { colors, alphaVar } from "../ui/colors";
import { useModals } from "../../context/ModalContext";
import { useVendors } from "./hooks/useVendors";
import { useVendorStore } from "./store/useVendorStore";
import { VendorCreate } from "./VendorCreate";
import { VendorForm } from "./VendorForm";
import { VendorsRow } from "./VendorsRow";

const GRAY = colors.gray;
const ORANGE = colors.orange;

const TABLE_COLUMNS = [
  { label: "Vendor", sortKey: "vendorCompanyName", hideOnMobile: false },
  { label: "Type", sortKey: "type", hideOnMobile: true },
  { label: "Status", sortKey: "status", hideOnMobile: true },
  { label: "Email", sortKey: null, hideOnMobile: false },
  { label: "Phone", sortKey: null, hideOnMobile: true },
];

interface VendorsTableProps {
  disableMenuOptions?: boolean;
  sx?: any;
  contact?: any;
  onLinkContact?: (vendor: any) => void | Promise<any>;
  initialPhoneNumber?: string;
  initialActiveTable?: "vendors" | "prospects";
  showContactsToggle?: boolean;
}

export const VendorsTable: React.FC<VendorsTableProps> = ({
  disableMenuOptions = false,
  sx,
  contact,
  onLinkContact,
  initialPhoneNumber,
  initialActiveTable = "vendors",
  showContactsToggle = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Active table tab state
  const [activeTable, setActiveTable] = useState<"vendors" | "prospects">(initialActiveTable || "vendors");

  // Search state
  const [filterBy, setFilterBy] = useState("name");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Sorting state
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderByColumn, setOrderByColumn] = useState("");

  const [detailsVendor, setDetailsVendor] = useState<any>(null);
  const detailsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { closeByKey } = useModals();

  const { loading: isLoadingVendors, getAllVendors, removeVendor } = useVendors();

  const { vendors } = useVendorStore();

  const openDetails = (vendor: any) => {
    setDetailsVendor(vendor);
    requestAnimationFrame(() => detailsTriggerRef.current?.click());
  };

  // Debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 700);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const loadVendors = () => {
    getAllVendors({
      page: page + 1,
      pageSize,
      orderBy: order,
      orderField: orderByColumn || "createdAt",
      search: debouncedSearch || undefined,
      filterBy: debouncedSearch ? filterBy : undefined,
      status: activeTable === "vendors" ? "ACTIVE" : "PROSPECT",
    });
  };

  useEffect(() => {
    loadVendors();
  }, [page, pageSize, debouncedSearch, filterBy, order, orderByColumn, activeTable]);

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortColumn = (column: string) => {
    const isAsc = orderByColumn === column && order === "asc";
    setOrder(isAsc ? "asc" : "desc");
    setOrderByColumn(column);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(0);
  };

  const removeVendorHandler = async (uid: string) => {
    await removeVendor(uid);
  };

  const getPlaceholder = (filter: string) => {
    const isProspect = activeTable === "prospects";
    switch (filter) {
      case "email":
        return "Search by email...";
      case "phone":
        return "Search by phone...";
      case "type":
        return "Search by type (INDEPENDENT, CORPORATE)...";
      case "name":
      default:
        return isProspect ? "Search prospect by name or company..." : "Search by name or company...";
    }
  };

  const visibleRows = vendors?.items ?? [];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        ...sx,
      }}
    >
      {showContactsToggle && !contact && (
        <Tabs
          value={activeTable}
          onChange={(_, val) => {
            setActiveTable(val);
            setPage(0);
          }}
          sx={{
            mb: 2,
            px: 3,
            width: "100%",
            flexShrink: 0,
            borderBottom: `1px solid ${alphaVar(ORANGE.base, 0.1)}`,
            alignSelf: "stretch",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9375rem",
              color: GRAY[600],
            },
            "& .MuiTab-root.Mui-selected": {
              color: ORANGE.base,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: ORANGE.base,
            },
          }}
        >
          <Tab label="Vendors" value="vendors" />
          <Tab label="Prospects" value="prospects" />
        </Tabs>
      )}

      <Box sx={{ width: "100%", position: "relative", display: "flex", pt: 2, flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* Controls Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
            mb: 2,
            width: "100%",
            justifyContent: "space-between",
            px: 3,
          }}
        >
          {/* Search bar */}
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
            <TextField
              inputRef={searchInputRef}
              size="small"
              placeholder={getPlaceholder(filterBy)}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPage(0);
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                  border: `1px solid rgba(12,12,12,.06)`,
                  boxShadow: "0 2px 8px rgba(12,12,12,.04)",
                  borderRadius: "8px 0 0 8px",
                  height: 40,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: GRAY[400], fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: !!searchValue && (
                  <Fade in>
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  </Fade>
                ),
              }}
            />
            <FormControl size="small">
              <Select
                value={filterBy}
                onChange={(e) => {
                  setFilterBy(e.target.value as string);
                  setPage(0);
                }}
                sx={{
                  minWidth: { xs: 70, sm: 120 },
                  minHeight: 40,
                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: "0 8px 8px 0",
                  border: `1px solid rgba(12,12,12,.06)`,
                  borderLeft: "none",
                  "& .MuiSelect-select": { py: "8.5px", fontSize: { xs: "0.75rem", sm: "0.875rem" }, pl: { xs: 1, sm: 2 } },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="type">Type</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Add Vendor / Vendor Prospect Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <BaseModalComponent
              modalKey="Dashboard-VendorAdd-Business"
              modalLabel={activeTable === "prospects" ? "Add Vendor Prospect" : "Add Vendor"}
              defaultSizes={{ width: 800 }}
              allowInsidePreventModal={true}
              elementTrigger={
                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: "none",
                    bgcolor: ORANGE.base,
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 2,
                    height: 40,
                    px: { xs: 1.5, sm: 3 },
                    "&:hover": { bgcolor: ORANGE[600] },
                  }}
                >
                  <Add sx={{ display: { xs: "block", sm: "none" } }} />
                  <Box component="span" sx={{ display: { xs: "none", sm: "block" } }}>
                    {activeTable === "prospects" ? "+ New Vendor Prospect" : "+ New Vendor"}
                  </Box>
                </Button>
              }
            >
              <VendorCreate
                onDone={loadVendors}
                initialPhoneNumber={initialPhoneNumber}
                status={activeTable === "prospects" ? "PROSPECT" : "ACTIVE"}
              />
            </BaseModalComponent>
          </Box>
        </Box>

        {/* Table wrapper */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: `1px solid rgba(12,12,12,.06)`,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(12,12,12,.04)",
            position: "relative",
            minHeight: "60vh",
            overflow: "auto",
            px: 3,
          }}
        >
          <PaginationLoadingOverlay isVisible={isLoadingVendors} />

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    "& th": {
                      fontWeight: 400,
                      color: GRAY[600],
                      borderBottom: `1px solid ${alphaVar(ORANGE.base, 0.2)}`,
                      fontSize: "0.8125rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    },
                  }}
                >
                  {TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.label}
                      sx={{
                        display: col.hideOnMobile ? { xs: "none", sm: "table-cell" } : "table-cell",
                        pl: 2,
                      }}
                    >
                      {col.sortKey ? (
                        <TableSortLabel
                          active={orderByColumn === col.sortKey}
                          direction={orderByColumn === col.sortKey ? order : "asc"}
                          onClick={() => handleSortColumn(col.sortKey)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                  {contact && onLinkContact && (
                    <TableCell align="right" sx={{ width: 100 }}>
                      Link Phone
                    </TableCell>
                  )}
                  {activeTable === "prospects" && (
                    <TableCell align="right" sx={{ width: 160 }}>
                      Convert
                    </TableCell>
                  )}
                  {!disableMenuOptions && (
                    <TableCell align="right" sx={{ width: 64 }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((vendorItem) => (
                  <VendorsRow
                    key={vendorItem.uid}
                    row={vendorItem}
                    disableMenuOptions={disableMenuOptions}
                    handleDelete={removeVendorHandler}
                    handleUpdate={loadVendors}
                    onLinkContact={onLinkContact}
                    contact={contact}
                    activeTable={activeTable}
                    onOpenModalDetails={openDetails}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={vendors?.totalItems || 0}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)" }}
          />
        </TableContainer>

        <BaseModalComponent
          modalKey={detailsVendor ? `update-vendor-${detailsVendor.uid}` : "update-vendor-modal"}
          modalLabel="Edit Vendor"
          defaultSizes={{ width: 800 }}
          elementTrigger={<button ref={detailsTriggerRef} type="button" style={{ position: "absolute", visibility: "hidden" }} />}
        >
          {detailsVendor && (
            <VendorForm
              mode="update"
              vendor={detailsVendor}
              vendorUid={detailsVendor.uid}
              onUpdate={async () => {
                closeByKey(detailsVendor ? `update-vendor-${detailsVendor.uid}` : "update-vendor-modal");
                await loadVendors();
              }}
            />
          )}
        </BaseModalComponent>
      </Box>
    </Box>
  );
};
export default VendorsTable;
